import React from 'react';
import { useAdminQuotaOverview } from '../hooks/useAdminQuotaOverview';
import { FiDatabase, FiAlertTriangle, FiCheckCircle, FiUsers, FiActivity, FiArrowLeft } from '../components/icons/Icons';
import { Link } from 'react-router-dom';

const AdminApiQuota = () => {
  const { overview, policies, loading, error, refetch } = useAdminQuotaOverview();

  if (loading && !overview) {
    return <div style={{ color: '#fff', padding: '40px' }}>Chargement de la supervision...</div>;
  }

  if (error && !overview) {
    return <div style={{ color: '#ff4d4d', padding: '40px' }}>{error}</div>;
  }

  const { redis_status, redis_error, top_consumers, fail_open_active } = overview;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d0d14',
      color: '#fff',
      padding: '40px',
      fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px', gap: '16px' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiArrowLeft /> Retour
        </Link>
        <h1 style={{ margin: 0, fontSize: '2rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FiActivity style={{ color: '#00ff94' }} /> Admin API Quota
        </h1>
        <button onClick={refetch} style={{
          marginLeft: 'auto', background: '#1a1a2e', color: '#fff', border: '1px solid #333', 
          padding: '8px 16px', borderRadius: '8px', cursor: 'pointer'
        }}>
          Rafraîchir
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '40px' }}>
        {/* Status Card */}
        <div style={{ background: '#161622', padding: '24px', borderRadius: '12px', border: '1px solid #2a2a3e' }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#aaa', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiDatabase /> État Redis
          </h3>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {redis_status === 'ok' ? (
              <><FiCheckCircle style={{ color: '#4caf50' }} /> Connecté et Opérationnel</>
            ) : (
              <><FiAlertTriangle style={{ color: '#ff4d4d' }} /> Dégradé (Down)</>
            )}
          </div>
          {redis_error && (
            <p style={{ color: '#ff4d4d', fontSize: '0.9rem', marginTop: '12px' }}>{redis_error}</p>
          )}
        </div>

        {/* Fail-Open Card */}
        <div style={{ background: '#161622', padding: '24px', borderRadius: '12px', border: '1px solid #2a2a3e' }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#aaa', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiAlertTriangle /> Mode Fail-Open
          </h3>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {fail_open_active ? (
              <span style={{ color: '#ff9800' }}>Actif (Sécurité engagée)</span>
            ) : (
              <span style={{ color: '#4caf50' }}>Inactif (Nominal)</span>
            )}
          </div>
          <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '12px' }}>
            Si Redis est inaccessible, les requêtes sont exceptionnellement autorisées pour ne pas impacter la production.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Top Consumers */}
        <div style={{ background: '#161622', padding: '24px', borderRadius: '12px', border: '1px solid #2a2a3e' }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#aaa', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiUsers /> Top Consommateurs (Actuels)
          </h3>
          {top_consumers.length === 0 ? (
            <p style={{ color: '#888' }}>Aucun utilisateur détecté actuellement.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', color: '#888' }}>
                  <th style={{ padding: '8px 0' }}>Utilisateur / Clé</th>
                  <th style={{ padding: '8px 0', textAlign: 'right' }}>Requêtes (Aujourd'hui)</th>
                </tr>
              </thead>
              <tbody>
                {top_consumers.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                    <td style={{ padding: '12px 0' }}><code>{c.user_id}</code></td>
                    <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold' }}>{c.daily_used}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Policies Map */}
        <div style={{ background: '#161622', padding: '24px', borderRadius: '12px', border: '1px solid #2a2a3e' }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#aaa' }}>Politiques de Quota (Profiles)</h3>
          {policies && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {Object.entries(policies).map(([key, config]) => (
                <div key={key} style={{ background: '#0d0d14', padding: '12px', borderRadius: '8px', border: '1px solid #222' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <strong style={{ color: '#00ff94' }}>{key}</strong>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{config.label}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', fontSize: '0.9rem', color: '#ccc', gap: '8px' }}>
                    <div>Quota: {config.daily_quota === -1 ? 'Illimité' : config.daily_quota} / jour</div>
                    <div>Cooldown: {config.min_interval_seconds}s</div>
                    <div>Bypass Quota: {config.bypass_quota ? 'Oui' : 'Non'}</div>
                    <div>Bypass Cooldown: {config.bypass_cooldown ? 'Oui' : 'Non'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminApiQuota;
